import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AdminTable from "components/admin/AdminTable";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { useMaterialUIController } from "context";
import { formatNumber } from "utils/format";

ChartJS.register(
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

/*
 * Sơ đồ người chơi online (dữ liệu máy chủ ghi 5 phút / lần, xem OnlineStats.cs):
 *   - 24 giờ: hôm nay (xanh, liền) so với hôm qua (cam, đứt) theo giờ trong ngày, nhãn ghi thẳng ở đầu mỗi đường.
 *   - 7 / 30 ngày: cột = số người cao nhất mỗi ngày.
 *   - "Xem dạng bảng" cho người không quen đọc biểu đồ.
 * Màu đã chạy validate_palette (dataviz) đạt cả nền sáng #ffffff và nền tối #202940.
 */
const PALETTE = {
  light: {
    today: "#2a78d6",
    yesterday: "#eb6834",
    grid: "#e9ecef",
    tick: "#7b809a",
    label: "#344767",
    surface: "#ffffff",
    crosshair: "rgba(52,71,103,0.35)",
  },
  dark: {
    today: "#3987e5",
    yesterday: "#d95926",
    grid: "rgba(255,255,255,0.08)",
    tick: "rgba(255,255,255,0.6)",
    label: "rgba(255,255,255,0.85)",
    surface: "#202940",
    crosshair: "rgba(255,255,255,0.35)",
  },
};

const RANGES = [
  { value: "day", label: "24 giờ" },
  { value: "7", label: "7 ngày" },
  { value: "30", label: "30 ngày" },
];

const pad = (n) => String(n).padStart(2, "0");
const slotLabel = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
const dayLabel = (iso) => {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Chia điểm thành hai dãy theo mốc trong ngày: [hôm qua, hôm nay], ô không có mẫu là null (máy chủ tắt). */
function splitDays(points, sampleMinutes) {
  const slots = Math.round((24 * 60) / sampleMinutes);
  const today = new Array(slots).fill(null);
  const yesterday = new Array(slots).fill(null);
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  points.forEach((p) => {
    const t = new Date(p.time);
    const slot = Math.floor((t.getHours() * 60 + t.getMinutes()) / sampleMinutes);
    if (isSameDay(t, now)) today[slot] = p.online;
    else if (isSameDay(t, yest)) yesterday[slot] = p.online;
  });
  return { today, yesterday, slots };
}

/** Tóm tắt cho ô số liệu trang Tổng quan: cao nhất hôm nay (lúc nào), cùng giờ hôm qua. */
export function summarizeHistory(history) {
  if (!history?.points?.length) return null;
  const { today, yesterday } = splitDays(history.points, history.sampleMinutes);
  let peak = null;
  let peakSlot = -1;
  today.forEach((v, i) => {
    if (v != null && (peak == null || v > peak)) {
      peak = v;
      peakSlot = i;
    }
  });
  const yesterdayPeak = yesterday.reduce(
    (m, v) => (v != null && (m == null || v > m) ? v : m),
    null
  );
  const now = new Date();
  const nowSlot = Math.floor((now.getHours() * 60 + now.getMinutes()) / history.sampleMinutes);
  // Cùng giờ hôm qua: lấy mẫu gần nhất về trước trong 30 phút.
  let sameTimeYesterday = null;
  for (let i = nowSlot; i >= 0 && i > nowSlot - 30 / history.sampleMinutes; i -= 1) {
    if (yesterday[i] != null) {
      sameTimeYesterday = yesterday[i];
      break;
    }
  }
  return {
    peak,
    peakTime: peakSlot >= 0 ? slotLabel(peakSlot * history.sampleMinutes) : null,
    yesterdayPeak,
    sameTimeYesterday,
  };
}

/** Ghi tên + số ở đầu mỗi đường (thay cho bắt người đọc dò chú thích). */
const endLabelPlugin = {
  id: "endLabels",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const placed = [];
    chart.data.datasets.forEach((ds, index) => {
      if (!ds.endLabel) return;
      const meta = chart.getDatasetMeta(index);
      if (meta.hidden) return;
      let last = -1;
      ds.data.forEach((v, i) => {
        if (v != null) last = i;
      });
      if (last < 0) return;
      const point = meta.data[last];
      const text = `${ds.endLabel}: ${formatNumber(ds.data[last])}`;
      ctx.save();
      ctx.font = "600 12px Roboto, Helvetica, Arial, sans-serif";
      const width = ctx.measureText(text).width;
      let x = point.x + 8;
      if (x + width > chartArea.right) x = point.x - width - 8;
      let y = point.y;
      placed.forEach((py) => {
        if (Math.abs(py - y) < 14) y = py + (y >= py ? 14 : -14);
      });
      y = Math.min(Math.max(y, chartArea.top + 8), chartArea.bottom - 8);
      placed.push(y);
      // Nền lót màu mặt thẻ để chữ không đè lên đường kia.
      ctx.fillStyle = ds.labelSurface;
      ctx.fillRect(x - 3, y - 9, width + 6, 18);
      ctx.fillStyle = ds.labelColor;
      ctx.textBaseline = "middle";
      ctx.fillText(text, x, y);
      ctx.restore();
    });
  },
};

/** Đường dọc theo chuột: người đọc nhắm theo giờ, không phải nhắm trúng đường 2px. */
const crosshairPlugin = {
  id: "crosshair",
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() || [];
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    const { x } = active[0].element;
    ctx.save();
    ctx.strokeStyle = chart.options.plugins.crosshair?.color || "rgba(0,0,0,0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

function LegendKey({ color, dashed, label }) {
  return (
    <MDBox display="flex" alignItems="center" gap={0.75}>
      <MDBox
        component="span"
        width={18}
        height={0}
        sx={{ borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` }}
      />
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
    </MDBox>
  );
}

LegendKey.defaultProps = { dashed: false };
LegendKey.propTypes = {
  color: PropTypes.string.isRequired,
  dashed: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

function OnlineChart({ history }) {
  const [controller] = useMaterialUIController();
  const colors = controller.darkMode ? PALETTE.dark : PALETTE.light;
  const [range, setRange] = useState("day");
  const [asTable, setAsTable] = useState(false);
  const daily = useFetch(
    () =>
      range === "day"
        ? Promise.resolve(null)
        : api.get("/api/admin/server/online-daily", { days: Number(range) }),
    [range]
  );

  const axes = useMemo(
    () => ({
      x: {
        grid: { display: false },
        border: { color: colors.grid },
        ticks: { color: colors.tick, font: { size: 11 }, autoSkip: true, maxRotation: 0 },
      },
      y: {
        beginAtZero: true,
        grid: { color: colors.grid, lineWidth: 1 },
        border: { display: false },
        ticks: { color: colors.tick, font: { size: 11 }, precision: 0, maxTicksLimit: 5 },
      },
    }),
    [colors]
  );

  const tooltip = {
    backgroundColor: controller.darkMode ? "#ffffff" : "#1a2035",
    titleColor: controller.darkMode ? "#344767" : "#ffffff",
    bodyColor: controller.darkMode ? "#344767" : "#ffffff",
    padding: 10,
    boxPadding: 4,
    usePointStyle: true,
  };

  const day = useMemo(() => {
    if (!history?.points) return null;
    const { today, yesterday, slots } = splitDays(history.points, history.sampleMinutes);
    const labels = Array.from({ length: slots }, (_, i) => slotLabel(i * history.sampleMinutes));
    return { labels, today, yesterday };
  }, [history]);

  const dayChart = day && {
    data: {
      labels: day.labels,
      // Hôm nay đứng trước (đầu khung chi tiết) và vẽ đè lên hôm qua (order nhỏ vẽ sau cùng).
      datasets: [
        {
          label: "Hôm nay",
          endLabel: "Hôm nay",
          labelColor: colors.label,
          labelSurface: colors.surface,
          order: 0,
          data: day.today,
          borderColor: colors.today,
          backgroundColor: `${colors.today}14`,
          fill: "origin",
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHitRadius: 12,
          tension: 0.25,
          spanGaps: false,
          pointStyle: "line",
        },
        {
          label: "Hôm qua",
          endLabel: "Hôm qua",
          labelColor: colors.label,
          labelSurface: colors.surface,
          order: 1,
          data: day.yesterday,
          borderColor: colors.yesterday,
          borderWidth: 2,
          borderDash: [5, 4],
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHitRadius: 12,
          tension: 0.25,
          spanGaps: false,
          pointStyle: "line",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      layout: { padding: { right: 96 } },
      plugins: {
        legend: { display: false },
        crosshair: { color: colors.crosshair },
        tooltip: {
          ...tooltip,
          callbacks: {
            labelColor: (item) => ({
              borderColor: item.dataset.borderColor,
              backgroundColor: item.dataset.borderColor,
              borderWidth: 2,
            }),
            title: (items) => `Lúc ${items[0].label}`,
            label: (item) =>
              item.raw == null
                ? `${item.dataset.label}: không có số liệu`
                : `${item.dataset.label}: ${formatNumber(item.raw)} người`,
          },
        },
      },
      scales: {
        ...axes,
        x: {
          ...axes.x,
          ticks: {
            ...axes.x.ticks,
            autoSkip: false,
            // Mốc 2 giờ; màn hẹp (điện thoại) mốc 6 giờ để chữ không chồng nhau.
            callback(value, index) {
              const hours = this.chart.width < 600 ? 6 : 2;
              return index % ((hours * 60) / history.sampleMinutes) === 0 ? day.labels[index] : "";
            },
          },
        },
      },
    },
  };

  const days = daily.data || [];
  const barChart = range !== "day" && {
    data: {
      labels: days.map((d) => dayLabel(d.day)),
      datasets: [
        {
          label: "Cao nhất",
          data: days.map((d) => d.peak),
          backgroundColor: colors.today,
          hoverBackgroundColor: `${colors.today}cc`,
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: "bottom",
          maxBarThickness: 28,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltip,
          displayColors: false,
          callbacks: {
            title: (items) => {
              const d = days[items[0].dataIndex];
              return `Ngày ${dayLabel(d.day)}${
                d.day === new Date().toLocaleDateString("sv-SE") ? " (hôm nay, tới giờ)" : ""
              }`;
            },
            label: (item) => {
              const d = days[item.dataIndex];
              return [
                `Cao nhất: ${formatNumber(d.peak)} người`,
                `Trung bình: ${formatNumber(d.average)} người`,
              ];
            },
          },
        },
      },
      scales: axes,
    },
  };

  const tableRows =
    range === "day"
      ? (day?.labels || [])
          .map((label, i) => ({ label, i }))
          .filter(({ i }) => i % (60 / (history?.sampleMinutes || 5)) === 0)
          .map(({ label, i }) => {
            const step = 60 / (history?.sampleMinutes || 5);
            const max = (arr) =>
              arr
                .slice(i, i + step)
                .reduce((m, v) => (v != null && (m == null || v > m) ? v : m), null);
            return {
              key: label,
              time: `${label.slice(0, 2)} giờ`,
              today: max(day.today),
              yesterday: max(day.yesterday),
            };
          })
          .filter((r) => r.today != null || r.yesterday != null)
          .reverse()
      : [...days].reverse().map((d) => ({ key: d.day, ...d }));

  const hasData = range === "day" ? !!history?.points?.length : days.length > 0;
  const loading = range === "day" ? history === undefined : daily.loading;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={2} pb={0} display="flex" flexWrap="wrap" gap={1} alignItems="flex-start">
        <MDBox flex={1} minWidth={200}>
          <MDTypography variant="h6">Người chơi online</MDTypography>
          <MDTypography variant="button" color="text">
            {range === "day"
              ? "Hôm nay so với hôm qua, theo giờ trong ngày"
              : `Số người cao nhất mỗi ngày, ${range} ngày gần đây`}
          </MDTypography>
        </MDBox>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={range}
          onChange={(e, v) => v && setRange(v)}
          sx={({ palette }) => ({
            "& .MuiToggleButton-root": { color: palette.text.main, px: 1.5 },
            "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
              color: palette.white.main,
              backgroundColor: palette.info.main,
            },
          })}
        >
          {RANGES.map((r) => (
            <ToggleButton key={r.value} value={r.value}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <MDButton
          size="small"
          variant="text"
          color="info"
          onClick={() => setAsTable((v) => !v)}
          disabled={!hasData}
        >
          <Icon>{asTable ? "show_chart" : "table_rows"}</Icon>&nbsp;
          {asTable ? "Xem biểu đồ" : "Xem dạng bảng"}
        </MDButton>
      </MDBox>

      {range === "day" && hasData && !asTable && (
        <MDBox px={2} pt={1} display="flex" gap={2}>
          <LegendKey color={colors.today} label="Hôm nay" />
          <LegendKey color={colors.yesterday} dashed label="Hôm qua" />
        </MDBox>
      )}

      <MDBox p={2} pt={1}>
        {history === null && range === "day" && (
          <MDTypography variant="button" color="text" display="block" py={6} textAlign="center">
            Không tải được số liệu online. Máy chủ game có thể chưa được cập nhật bản ghi số người
            online — báo kỹ thuật.
          </MDTypography>
        )}
        {daily.error && range !== "day" && (
          <MDTypography variant="button" color="text" display="block" py={6} textAlign="center">
            Không tải được số liệu: {daily.error}
          </MDTypography>
        )}
        {!loading && history !== null && !daily.error && !hasData && (
          <MDTypography variant="button" color="text" display="block" py={6} textAlign="center">
            Chưa có số liệu. Máy chủ ghi số người online 5 phút một lần — quay lại sau ít phút.
          </MDTypography>
        )}
        {hasData && asTable && (
          <MDBox maxHeight={300} overflow="auto">
            <AdminTable
              rows={tableRows}
              rowKey="key"
              columns={
                range === "day"
                  ? [
                      { key: "time", label: "Giờ" },
                      {
                        key: "today",
                        label: "Hôm nay (cao nhất)",
                        align: "right",
                        render: (r) => (r.today == null ? "-" : formatNumber(r.today)),
                      },
                      {
                        key: "yesterday",
                        label: "Hôm qua (cao nhất)",
                        align: "right",
                        render: (r) => (r.yesterday == null ? "-" : formatNumber(r.yesterday)),
                      },
                    ]
                  : [
                      { key: "day", label: "Ngày", render: (r) => dayLabel(r.day) },
                      {
                        key: "peak",
                        label: "Cao nhất",
                        align: "right",
                        render: (r) => formatNumber(r.peak),
                      },
                      {
                        key: "average",
                        label: "Trung bình",
                        align: "right",
                        render: (r) => formatNumber(r.average),
                      },
                    ]
              }
            />
          </MDBox>
        )}
        {hasData && !asTable && (
          <MDBox height={300} sx={{ opacity: daily.loading && range !== "day" ? 0.5 : 1 }}>
            {range === "day" ? (
              <Line
                data={dayChart.data}
                options={dayChart.options}
                plugins={[endLabelPlugin, crosshairPlugin]}
              />
            ) : (
              <Bar data={barChart.data} options={barChart.options} />
            )}
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

// undefined = đang tải, null = lỗi (máy chủ cũ chưa có API), object = số liệu.
OnlineChart.defaultProps = { history: undefined };
OnlineChart.propTypes = {
  history: PropTypes.shape({
    sampleMinutes: PropTypes.number,
    points: PropTypes.arrayOf(
      PropTypes.shape({ time: PropTypes.string, online: PropTypes.number })
    ),
  }),
};

export default OnlineChart;
